import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import GlobalHeader from "../../components/ui/GlobalHeader";
import SidebarNavigation from "../../components/ui/SidebarNavigation";
import BreadcrumbTrail from "../../components/ui/BreadcrumbTrail";
import QuickActionToolbar from "../../components/ui/QuickActionToolbar";
import CouponStats from "./components/CouponStats";
import CouponFilters from "./components/CouponFilters";
import CouponTable from "./components/CouponTable";
import CouponModal from "./components/CouponModal";

import Button from "../../components/ui/Button";
import NavigationHeader from "components/ui/NavigationHeader";
import MainNavigation from "components/ui/MainNavigation";
import { useTranslation } from "react-i18next";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteDiscountCodeService,
  getDiscountCouponsService,
} from "redux/services/general_service";
import { toast } from "react-toastify";
import { getAllCategoriesService } from "redux/services/categories_service";

const CouponManagement = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const { discountCode, metaDiscountCode } = useSelector(
    (state) => state.general
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [selectedCoupons, setSelectedCoupons] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    discountType: "all",
    dateFrom: "",
    dateTo: "",
    minDiscount: "",
    maxDiscount: "",
  });

  // Mock data for coupons
  const [coupons, setCoupons] = useState(discountCode);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Pagination
  const totalPages = Math.ceil(metaDiscountCode?.total / itemsPerPage);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  // Calculate stats
  const stats = {
    total: coupons?.length,
    active: coupons?.filter((c) => c?.status === "active")?.length,
    expired: coupons?.filter((c) => c?.status === "expired")?.length,
    totalUsage: coupons?.reduce((sum, c) => sum + c?.usageCount, 0),
  };
  // Mock user data
  const user = {
    name: t("userName"),
    email: t("userEmail"),
    avatar:
      "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
  };
  // Filter and sort coupons
  const filteredCoupons = coupons?.filter((coupon) => {
    const matchesSearch =
      !filters?.search ||
      coupon?.code?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      coupon?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase());

    const matchesStatus =
      filters?.status === "all" || coupon?.status === filters?.status;

    const matchesDiscountType =
      filters?.discountType === "all" ||
      coupon?.discountType === filters?.discountType;

    const matchesDateRange =
      (!filters?.dateFrom ||
        new Date(coupon.validFrom) >= new Date(filters.dateFrom)) &&
      (!filters?.dateTo ||
        new Date(coupon.validTo) <= new Date(filters.dateTo));

    const matchesDiscountRange =
      (!filters?.minDiscount ||
        coupon?.discountValue >= parseFloat(filters?.minDiscount)) &&
      (!filters?.maxDiscount ||
        coupon?.discountValue <= parseFloat(filters?.maxDiscount));

    return (
      matchesSearch &&
      matchesStatus &&
      matchesDiscountType &&
      matchesDateRange &&
      matchesDiscountRange
    );
  });

  const sortedCoupons = [...filteredCoupons]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];

    if (aValue < bValue) return sortConfig?.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev?.key === key && prev?.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSelectCoupon = (couponId, selected) => {
    setSelectedCoupons((prev) =>
      selected ? [...prev, couponId] : prev?.filter((id) => id !== couponId)
    );
  };

  const handleSelectAll = (selected) => {
    setSelectedCoupons(selected ? sortedCoupons?.map((c) => c?.id) : []);
  };

  const handleCreateCoupon = () => {
    setSelectedCoupon(null);
    setModalMode("create");
    setShowCouponModal(true);
  };

  const handleEditCoupon = (coupon) => {
    setSelectedCoupon(coupon);
    setModalMode("edit");
    setShowCouponModal(true);
  };

  const handleDuplicateCoupon = (coupon) => {
    const duplicatedCoupon = {
      code: `${coupon?.code}_COPY`,
    };

    setCoupons((prev) => [duplicatedCoupon, ...prev]);
  };

  const handleDeleteCoupon = (coupon) => {
    if (
      window.confirm(
        `Are you sure you want to delete the coupon "${coupon?.code}"?`
      )
    ) {
      dispatch(deleteDiscountCodeService({ id: coupon?.uuid })).then(
        (response) => {
          if (
            response?.payload?.status == 200 ||
            response?.payload?.status == 204
          ) {
            toast("Deleted Success");
            dispatch(getDiscountCouponsService({ page: 1 }));
          }
        }
      );
      //  setCoupons((prev) => prev?.filter((c) => c?.id !== coupon?.id));
      // setSelectedCoupons((prev) => prev?.filter((id) => id !== coupon?.id));
    }
  };

  const handleToggleStatus = (coupon) => {
    const newStatus = coupon?.status === "active" ? "inactive" : "active";
    setCoupons((prev) =>
      prev?.map((c) =>
        c?.id === coupon?.id
          ? { ...c, status: newStatus, updatedAt: new Date()?.toISOString() }
          : c
      )
    );
  };

  const handleSaveCoupon = (couponData) => {
    if (modalMode === "create") {
      const newCoupon = {
        ...couponData,
        id: Date.now(),
        usageCount: 0,
        createdAt: new Date()?.toISOString(),
      };
      setCoupons((prev) => [newCoupon, ...prev]);
    } else {
      setCoupons((prev) =>
        prev?.map((c) =>
          c?.id === selectedCoupon?.id ? { ...couponData, id: c?.id } : c
        )
      );
    }

    setShowCouponModal(false);
    setSelectedCoupon(null);
  };

  const handleBulkAction = (action) => {
    const selectedCouponObjects = coupons?.filter((c) =>
      selectedCoupons?.includes(c?.id)
    );

    switch (action) {
      case "activate":
        setCoupons((prev) =>
          prev?.map((c) =>
            selectedCoupons?.includes(c?.id)
              ? { ...c, status: "active", updatedAt: new Date()?.toISOString() }
              : c
          )
        );
        break;
      case "deactivate":
        setCoupons((prev) =>
          prev?.map((c) =>
            selectedCoupons?.includes(c?.id)
              ? {
                  ...c,
                  status: "inactive",
                  updatedAt: new Date()?.toISOString(),
                }
              : c
          )
        );
        break;
      case "delete":
        if (
          window.confirm(
            `Are you sure you want to delete ${selectedCoupons?.length} selected coupons?`
          )
        ) {
          setCoupons((prev) =>
            prev?.filter((c) => !selectedCoupons?.includes(c?.id))
          );
          setSelectedCoupons([]);
        }
        break;
      case "export":
        handleExport(selectedCouponObjects);
        break;
    }

    if (action !== "delete" && action !== "export") {
      setSelectedCoupons([]);
    }
  };

  const handleExport = (couponsToExport = sortedCoupons) => {
    const csvContent = [
      [
        "Code",
        "Name",
        "Type",
        "Value",
        "Valid From",
        "Valid To",
        "Usage",
        "Status",
      ]?.join(","),
      ...couponsToExport?.map((coupon) =>
        [
          coupon?.code,
          coupon?.name,
          coupon?.discountType,
          coupon?.discountValue,
          coupon?.validFrom,
          coupon?.validTo,
          `${coupon?.usageCount}/${coupon?.usageLimit || "∞"}`,
          coupon?.status,
        ]?.join(",")
      ),
    ]?.join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `coupons-${new Date()?.toISOString()?.split("T")?.[0]}.csv`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);
  };

  useEffect(() => {
    dispatch(getDiscountCouponsService({ page: 1 }));
    dispatch(getAllCategoriesService({ page: 1 }));
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Coupon Management </title>
        <meta
          name="description"
          content="Manage discount coupons, create promotional campaigns, and track coupon performance with comprehensive analytics."
        />
      </Helmet>
      {/* Navigation Header 
      <NavigationHeader
        user={user}
        onMenuToggle={handleSidebarToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

     
      <MainNavigation
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />*/}

      {/* Breadcrumb Navigation 
        <BreadcrumbNavigation />*/}
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t("coupon_management")}
              </h1>
            </div>

            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline" 
                fill_background_color="#FFFFFF"
                text_color="#000000"
                onClick={() => handleExport()}
                iconName="Download"
                iconPosition="left"
              >
                {t("export")}
              </Button>

              <Button
                variant="default"
                onClick={handleCreateCoupon}
                iconName="Plus"
                iconPosition="left"
              >
                {t("create_coupon")}
              </Button>
            </div>
          </div>

          <CouponStats stats={stats} />

          {/* <CouponFilters
            filters={filters}
            onFiltersChange={setFilters}
            onExport={() => handleExport()}
            onBulkAction={handleBulkAction}
            selectedCount={selectedCoupons?.length}
          />*/}

          <CouponTable
            coupons={discountCode}
            selectedCoupons={selectedCoupons}
            onSelectCoupon={handleSelectCoupon}
            onSelectAll={handleSelectAll}
            onSort={handleSort}
            sortConfig={sortConfig}
            onEdit={handleEditCoupon}
            onDuplicate={handleDuplicateCoupon}
            onDelete={handleDeleteCoupon}
            onToggleStatus={handleToggleStatus}
          />

          {/* Pagination */}

          <div className="flex items-center justify-center mt-6 space-x-2">
         

            <div className="flex justify-center py-4 space-x-2">
              <button
                variant="outline"
                  className="px-4 py-2 border rounded-md select-none hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  setCurrentPage(currentPage - 1);
                  dispatch(
                    getDiscountCouponsService({ page: currentPage - 1 })
                  );
                }}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                {t("previous")}
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={i}
                      className={`px-4 mr-2 ml-2 py-2 rounded-md select-none border ${
                        page === currentPage
                          ? "bg-green-200 border-green-400 text-green-900"
                          : "border-gray-300 hover:bg-green-50"
                      }`}
                      variant={currentPage === page ? "default" : "outline"}
                      onClick={() => {
                        dispatch(getDiscountCouponsService({ page: page }));
                        setCurrentPage(page);
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                className="px-4 py-2 border rounded-md select-none hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage === totalPages}
                onClick={() => {
                  setCurrentPage(currentPage + 1);
                  dispatch(
                    getDiscountCouponsService({ page: currentPage + 1 })
                  );
                }}
                iconName="ChevronRight"
                iconPosition="right"
              >
                {t("next")}
              </button>
            </div>
          </div>

          {/* Results Summary 
            <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Showing {sortedCoupons?.length} of {coupons?.length} coupons
              </span>

              {selectedCoupons?.length > 0 && (
                <span>
                  {selectedCoupons?.length} coupon
                  {selectedCoupons?.length !== 1 ? "s" : ""} selected
                </span>
              )}
            </div>*/}
        </div>
      </div>

      <CouponModal
        isOpen={showCouponModal}
        onClose={() => {
          setShowCouponModal(false);
          setSelectedCoupon(null);
        }}
        coupon={selectedCoupon}
        onSave={handleSaveCoupon}
        mode={modalMode}
      />
    </div>
  );
};

export default CouponManagement;
